import { useEffect, useRef, useState } from 'react';

/**
 * Top Charts 筛选器吸顶 hook：
 *   - 单一状态 filterFixed
 *   - ResizeObserver 测「ref 元素 offsetTop + offsetHeight」=> minHeight
 *   - 滚动监听：scrollTop > minHeight ? 吸顶 : 还原
 *
 */

interface UseFilterStickyOptions {
  /** 是否启用吸顶；false 时所有副作用都不挂载 */
  enabled: boolean;
  /** 被观测的 filter 包装节点 ref（用于测高 + 阈值计算 + 滚动容器探测） */
  ref: React.RefObject<HTMLDivElement>;
  /** 可选：显式指定滚动容器；不传时自动探测 */
  scrollContainer?: HTMLElement | null;
}

export interface UseFilterStickyResult {
  /** 是否进入吸顶模式 */
  filterFixed: boolean;
  /** ref 元素当前高度 */
  filterHeight: number;
}

/**  沿父链向上找滚动容器 */
const findScrollContainer = (el: HTMLElement | null): HTMLElement | Window => {
  let cur = el?.parentElement ?? null;
  let firstScrollable: HTMLElement | null = null;
  while (cur && cur !== document.body && cur !== document.documentElement) {
    if (cur.classList?.contains('affix-target')) {
      return cur;
    }
    if (!firstScrollable) {
      const overflowY = window.getComputedStyle(cur).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        firstScrollable = cur;
      }
    }
    cur = cur.parentElement;
  }
  return firstScrollable || window;
};

const getScrollTop = (target: HTMLElement | Window): number => {
  if (target === window) {
    return window.pageYOffset || document.documentElement.scrollTop || 0;
  }
  return (target as HTMLElement).scrollTop;
};

export const useFilterSticky = ({
  enabled,
  ref,
  scrollContainer,
}: UseFilterStickyOptions): UseFilterStickyResult => {
  const [filterFixed, setFilterFixed] = useState(false);
  const [filterHeight, setFilterHeight] = useState(0);
  // 阈值
  const minHeightRef = useRef(0);
  const filterFixedRef = useRef(false);
  filterFixedRef.current = filterFixed;

  // 测量 ref 元素的实际高度 + 阈值
  useEffect(() => {
    if (!enabled || typeof ResizeObserver === 'undefined') return undefined;
    const el = ref.current;
    if (!el) return undefined;

    const scrollTarget = scrollContainer || findScrollContainer(el);

    const measure = () => {
      if (filterFixedRef.current) return;

      const height = el.offsetHeight || 0;
      setFilterHeight((prev) => (Math.abs(prev - height) > 1 ? height : prev));

      const currentScrollTop = getScrollTop(scrollTarget);
      const elRect = el.getBoundingClientRect();
      const containerTop =
        scrollTarget === window ? 0 : (scrollTarget as HTMLElement).getBoundingClientRect().top;
      const elTopInContainer = elRect.top - containerTop + currentScrollTop;

      // 阈值 = 元素底部在滚动容器坐标系中的位置
      // 当 scrollTop > 这个值时，筛选器完全滚出可视区域
      minHeightRef.current = elTopInContainer + height;
    };
    measure();

    let rafId = 0;
    const ro = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        measure();
      });
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [enabled, ref, scrollContainer]);

  // 滚动监听
  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === 'undefined') return undefined;

    const refEl = ref.current;
    const target: HTMLElement | Window = scrollContainer || findScrollContainer(refEl);

    const handle = () => {
      const scrollTop = getScrollTop(target);
      const next = scrollTop > minHeightRef.current;
      setFilterFixed((prev) => (prev !== next ? next : prev));
    };

    if (target === window) {
      window.addEventListener('scroll', handle, { passive: true, capture: true });
    } else {
      (target as HTMLElement).addEventListener('scroll', handle, { passive: true, capture: true });
    }

    return () => {
      if (target === window) {
        window.removeEventListener('scroll', handle, true);
      } else {
        (target as HTMLElement).removeEventListener('scroll', handle, true);
      }
    };
  }, [enabled, ref, scrollContainer]);

  if (!enabled) {
    return { filterFixed: false, filterHeight: 0 };
  }
  return { filterFixed, filterHeight };
};

export default useFilterSticky;
